import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ROLES } from '../data/roles';
import { SECTIONS } from '../data/sections';
import { matchIntent, buildContext, getQuickSuggestions } from '../utils/chatEngine';
import './ChatPanel.css';

function formatText(text) {
  const lines = text.split('\n');
  return lines.map((line, li) => {
    const parts = line.split(/\*\*(.*?)\*\*/g);
    const rich = parts.map((part, pi) =>
      pi % 2 === 1
        ? <strong key={pi}>{part}</strong>
        : part.split(/\*(.*?)\*/g).map((p2, qi) =>
            qi % 2 === 1 ? <em key={qi} style={{ color: 'var(--text-secondary)' }}>{p2}</em> : p2
          )
    );
    return <span key={li}>{rich}{li < lines.length - 1 && <br />}</span>;
  });
}

const WELCOME_MSG = (role) => ({
  id: 'welcome',
  from: 'bot',
  text: role
    ? `Hi! I'm your process guide assistant. I can see you're set up as **${ROLES[role]?.label || role}**. Ask me anything about rules, steps, or what to do next — or type **"What's next?"** to get started.`
    : `Hi! I'm your SWISO TECH process guide assistant.\n\nI can help you find your next action, understand rules, track progress, and navigate the handbook.\n\nStart by telling me your role — for example: *"I'm a Project Manager"* or *"I'm a Developer"*.`,
  suggestions: role
    ? ["What's my next step?", 'My progress', 'Show my sections', 'I need help']
    : ["I'm a Project Manager", "I'm a Developer", "I'm a Tester", "I'm a Functional Consultant"],
});

export default function ChatPanel({ state, currentSection, onNavigate, onSetRole }) {
  const [open, setOpen]       = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput]     = useState('');
  const [typing, setTyping]   = useState(false);
  const [unread, setUnread]   = useState(0);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  // Rebuild context fresh each render — captures latest state
  const getCtx = useCallback(
    () => buildContext(state, currentSection),
    [state, currentSection]
  );

  // Init welcome message when first opened
  useEffect(() => {
    if (open) {
      if (messages.length === 0) {
        setMessages([WELCOME_MSG(state.role)]);
      }
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 120);
    }
  }, [open]); // eslint-disable-line

  // If role changes externally, push a context update into chat
  const prevRole = useRef(state.role);
  useEffect(() => {
    if (prevRole.current !== state.role && messages.length > 0) {
      prevRole.current = state.role;
      if (state.role) {
        const r = ROLES[state.role];
        const note = {
          id: Date.now(),
          from: 'bot',
          text: `Your role has been set to **${r.label}**. I now have full context for your **${r.sections.length} sections**. Ask me anything!`,
          suggestions: ["What's my next step?", 'My responsibilities', 'My progress'],
        };
        setMessages(prev => [...prev, note]);
        if (!open) setUnread(u => u + 1);
      }
    }
  }, [state.role]); // eslint-disable-line

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  const sendMessage = useCallback((text) => {
    if (!text.trim()) return;
    const userMsg = { id: Date.now(), from: 'user', text: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setTyping(true);

    setTimeout(() => {
      const ctx = getCtx();
      const response = matchIntent(text, ctx, (newRole) => {
        if (onSetRole) onSetRole(newRole);
      });
      const botMsg = { id: Date.now() + 1, from: 'bot', ...response };
      setMessages(prev => [...prev, botMsg]);
      setTyping(false);
      if (!open) setUnread(u => u + 1);
    }, 500 + Math.random() * 300);
  }, [getCtx, onSetRole, open]);

  const handleAction = (action) => {
    if (action.section) onNavigate('section', action.section);
    else if (action.page) onNavigate(action.page);
    setOpen(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  const ctx = getCtx();
  const suggestions = getQuickSuggestions(ctx);

  return (
    <>
      {/* FAB */}
      <button
        className={`chat-fab${open ? ' chat-fab--open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close chat' : 'Open process guide chat'}
      >
        {open
          ? <i className="ti ti-x" aria-hidden="true" />
          : <i className="ti ti-message-2" aria-hidden="true" />
        }
        {!open && unread > 0 && <span className="chat-fab-badge">{unread}</span>}
      </button>

      {/* Panel */}
      {open && (
        <div className="chat-panel" role="dialog" aria-label="Process guide assistant">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-left">
              <div className="chat-avatar">
                <i className="ti ti-robot" style={{ fontSize: 16, color: '#fff' }} aria-hidden="true" />
              </div>
              <div>
                <div className="chat-header-title">Process Guide Assistant</div>
                <div className="chat-header-sub">
                  {state.role && ctx.overall
                    ? `${ROLES[state.role]?.label} · ${ctx.overall.pct}% complete`
                    : 'Ask me anything about the handbook'}
                </div>
              </div>
            </div>
            <button className="chat-close-btn" onClick={() => setOpen(false)} aria-label="Close chat">
              <i className="ti ti-x" aria-hidden="true" />
            </button>
          </div>

          {/* Context bar — shows current section */}
          {currentSection && (
            <div className="chat-context-bar">
              <i className="ti ti-map-pin" style={{ fontSize: 11 }} aria-hidden="true" />
              Viewing: <strong style={{ marginLeft: 3 }}>{currentSection} — {state.role && ROLES[state.role] ? '' : ''}</strong>
              <span style={{ marginLeft: 2, color: 'var(--brand)' }}>
                {SECTIONS[currentSection]?.title}
              </span>
            </div>
          )}

          {/* Messages */}
          <div className="chat-messages">
            {messages.map(msg => (
              <div key={msg.id} className={`chat-msg chat-msg--${msg.from}`}>
                {msg.from === 'bot' && (
                  <div className="chat-bot-icon">
                    <i className="ti ti-robot" style={{ fontSize: 12 }} aria-hidden="true" />
                  </div>
                )}
                <div className="chat-bubble">
                  <div className="chat-text">{formatText(msg.text)}</div>

                  {msg.actions?.length > 0 && (
                    <div className="chat-actions">
                      {msg.actions.map((a, i) => (
                        <button key={i} className="chat-action-btn" onClick={() => handleAction(a)}>
                          <i className="ti ti-arrow-right" style={{ fontSize: 11 }} aria-hidden="true" />
                          {a.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {msg.suggestions?.length > 0 && (
                    <div className="chat-suggestions">
                      {msg.suggestions.map((s, i) => (
                        <button key={i} className="chat-suggestion-pill" onClick={() => sendMessage(s)}>
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {typing && (
              <div className="chat-msg chat-msg--bot">
                <div className="chat-bot-icon">
                  <i className="ti ti-robot" style={{ fontSize: 12 }} aria-hidden="true" />
                </div>
                <div className="chat-bubble">
                  <div className="chat-typing"><span /><span /><span /></div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick suggestions */}
          <div className="chat-quick">
            {suggestions.map((s, i) => (
              <button key={i} className="chat-quick-pill" onClick={() => sendMessage(s)}>{s}</button>
            ))}
          </div>

          {/* Input */}
          <div className="chat-input-row">
            <input
              ref={inputRef}
              className="chat-input"
              type="text"
              placeholder="Ask about any rule, step, or process..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              aria-label="Chat input"
            />
            <button
              className="chat-send-btn"
              onClick={() => sendMessage(input)}
              disabled={!input.trim()}
              aria-label="Send message"
            >
              <i className="ti ti-send" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
